import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";

const KINDS = [
  { value: "counseling", label: "Counseling" },
  { value: "written_warning", label: "Written warning" },
  { value: "suspension", label: "Suspension" },
  { value: "follow_up", label: "Follow-up" },
  { value: "other", label: "Other" },
] as const;

export type DisciplinaryEvent = {
  id: number;
  occurredOn: string;
  kind: (typeof KINDS)[number]["value"];
  summary: string;
  outcome: string | null;
  managerName: string | null;
  recordedByName: string | null;
};

function kindLabel(kind: string): string {
  return KINDS.find((entry) => entry.value === kind)?.label ?? kind;
}

export function PersonDisciplinarySection(props: {
  staffId: number;
  events: DisciplinaryEvent[];
  writesAllowed: boolean;
  readOnlyHint: string;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [occurredOn, setOccurredOn] = useState("");
  const [kind, setKind] = useState<(typeof KINDS)[number]["value"]>("counseling");
  const [summary, setSummary] = useState("");
  const [outcome, setOutcome] = useState("");

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/people/staff/${props.staffId}/disciplinary`, {
        method: "POST",
        body: { occurredOn, kind, summary, outcome },
      }),
    onSuccess: async () => {
      setSummary("");
      setOutcome("");
      await queryClient.invalidateQueries({ queryKey: ["people", "staff", props.staffId] });
      showToast({ message: "Disciplinary record saved.", tone: "success" });
    },
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  return (
    <HubSettingsSection
      title="Disciplinary record"
      hoverHint="Visible to this person's managers and to the disciplinary reviewers named on the org chart. The manager stored on a note is the manager at the time it was written."
    >
      {props.events.length === 0 ? (
        <p className="mb-3 text-sm text-slate-600">No disciplinary records.</p>
      ) : (
        <ul className="mb-4 space-y-3">
          {props.events.map((event) => (
            <li key={event.id} className="rounded-md border border-slate-200 p-3 text-sm">
              <p className="font-medium">
                {event.occurredOn} · {kindLabel(event.kind)}
              </p>
              <p className="mt-1 whitespace-pre-wrap">{event.summary}</p>
              {event.outcome ? <p className="mt-1 text-slate-700">Outcome: {event.outcome}</p> : null}
              <p className="mt-1 text-slate-500">
                {event.managerName ? `Manager ${event.managerName}` : "No manager recorded"}
                {event.recordedByName ? ` · Recorded by ${event.recordedByName}` : ""}
              </p>
            </li>
          ))}
        </ul>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <Label htmlFor="discipline-date">Date</Label>
          <Input
            id="discipline-date"
            type="date"
            className="mt-1"
            value={occurredOn}
            disabled={!props.writesAllowed}
            onChange={(event) => setOccurredOn(event.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="discipline-kind">Kind</Label>
          <select
            id="discipline-kind"
            className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
            value={kind}
            disabled={!props.writesAllowed}
            onChange={(event) => setKind(event.target.value as (typeof KINDS)[number]["value"])}
          >
            {KINDS.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="discipline-summary">Summary</Label>
          <textarea
            id="discipline-summary"
            className="mt-1 min-h-24 w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
            value={summary}
            disabled={!props.writesAllowed}
            onChange={(event) => setSummary(event.target.value)}
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="discipline-outcome">Outcome</Label>
          <Input
            id="discipline-outcome"
            className="mt-1"
            value={outcome}
            disabled={!props.writesAllowed}
            onChange={(event) => setOutcome(event.target.value)}
          />
        </div>
      </div>
      <div className="mt-3">
        <ReadOnlyHoverLayer active={!props.writesAllowed} hoverHint={props.readOnlyHint}>
          <Button
            type="button"
            disabled={!props.writesAllowed || occurredOn === "" || summary.trim() === "" || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            Add record
          </Button>
        </ReadOnlyHoverLayer>
      </div>
    </HubSettingsSection>
  );
}
